<template>
  <div class="excel-import-container">
    <el-page-header content="串码导入" />
    <el-card class="import-card" shadow="hover">
      <!-- 上传区域：复用统一交互逻辑，适配单Sheet特性 -->
      <div class="upload-section">
        <!-- 导入人信息：保持统一展示风格 -->
        <div class="importer-info" v-if="importerName">
          <i class="el-icon-user"></i>
          <span class="label">当前导入人：</span>
          <span class="name">{{ importerName }}</span>
        </div>

        <el-upload
          class="excel-uploader"
          drag
          action="#"
          :auto-upload="false"
          :file-list="[]"
          :show-file-list="false"
          accept=".xlsx,.xls"
          :before-upload="beforeUpload"
          :on-change="handleFileChange"
          :on-remove="handleFileRemove"
          :limit="1"
          :on-exceed="handleExceed"
        >
          <i class="el-icon-upload2 upload-icon"></i>
          <div class="el-upload__text">
            将Excel文件拖到此处，或<em>点击上传</em>
          </div>
          <div class="el-upload__tip" slot="tip">
            仅支持.xlsx/.xls格式，文件大小不超过1000MB，当前支持 **1个Sheet** 串码数据
          </div>
        </el-upload>

        <div class="custom-file-list" v-if="fileList.length > 0">
          <div class="file-item">
            <i class="el-icon-file-excel file-icon"></i>
            <span class="file-name" :title="fileList[0].name">{{ fileList[0].name }}</span>
            <el-button
              type="text"
              icon="el-icon-delete"
              class="delete-btn"
              @click="handleFileRemove"
            ></el-button>
          </div>
        </div>

        <div class="btn-group" v-if="fileList.length > 0">
          <el-button
            type="primary"
            icon="el-icon-read"
            @click="parseExcel"
            :loading="isParsing"
            :disabled="!hasToken || isParsing || isImporting"
          >
            解析Excel数据
          </el-button>
          <el-button
            type="success"
            icon="el-icon-upload"
            @click="submitImport"
            :loading="isImporting"
            :disabled="!hasToken || !parsedData.length || isParsing || isImporting"
          >
            提交导入
          </el-button>
          <el-progress 
            v-if="isParsing && parseProgress > 0" 
            :percentage="parseProgress" 
            size="small" 
            style="width: 200px; margin-left: 20px;"
          />
        </div>
      </div>

      <!-- 预览区域：保持统一分页逻辑，适配单Sheet数据 -->
      <div class="preview-section" v-if="parsedData.length > 0">
        <el-divider content-position="left">
          <i class="el-icon-data-analysis"></i> 数据预览（共{{ parsedData.length }}条串码数据，Sheet名：{{ sheetName || '串码数据' }}）
        </el-divider>
        <el-table
          :data="currentPageData"
          border
          stripe
          size="small"
          max-height="400"
          v-loading="isTableLoading"
          :row-key="(row, idx) => idx"
          :highlight-current-row="false"
        >
          <el-table-column
            v-for="(prop, propIndex) in Object.keys(parsedData[0] || {})"
            :key="propIndex"
            :prop="prop"
            :label="prop"
            align="center"
            :width="Math.min(Math.max(prop.length * 8 + 40, 80), 250)"
            show-overflow-tooltip
          />
        </el-table>

        <div class="pagination-container" v-if="parsedData.length > 0">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="pagination.currentPage"
            :page-sizes="[50, 100, 200, 500]"
            :page-size="pagination.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="parsedData.length"
            background
            small
          />
        </div>

        <div class="data-count-tip" v-if="parsedData.length > 1000">
          <i class="el-icon-warning"></i> 当前数据量较大（{{ parsedData.length }}条），已分页展示，可直接提交
        </div>
        <div class="data-count-tip info" v-else-if="parsedData.length > 500">
          <i class="el-icon-info"></i> 当前数据量较多，分页展示更流畅，建议直接提交导入
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import service from '@/utils/request'
import * as XLSX from 'xlsx'
import router from '@/router'

export default {
  name: 'SerialCodeImport',
  data() {
    return {
      fileList: [],
      rawFile: null,
      isParsing: false,
      isImporting: false,
      isTableLoading: false,
      parseProgress: 0,
      parsedData: [], // 存储单Sheet解析后的数据（行数据列表）
      sheetName: '', // 新增：存储单Sheet的原始名称（用于统一提交结构）
      importerName: '', // 导入人（当前登录人）
      pagination: { // 统一：分页参数结构
        currentPage: 1,
        pageSize: 100
      },
      currentPageData: []
    }
  },
  computed: {
    hasToken() {
      const token = localStorage.getItem('accessToken')
      // 统一：校验token存在且非空字符串
      return !!token && token.trim() !== ''
    },
    hasValidImporter() {
      // 统一：校验导入人信息有效性
      return !!this.importerName && this.importerName.trim() && this.importerName !== 'undefined'
    }
  },
  watch: {
    pagination: {
      handler() {
        this.setCurrentPageData()
      },
      deep: true
    },
    // 统一：token失效时清空所有数据
    hasToken(newVal) {
      if (!newVal) {
        this.parsedData = []
        this.sheetName = ''
        this.fileList = []
        this.rawFile = null
        this.importerName = ''
      }
    },
    // 统一：导入人无效时跳转登录
    hasValidImporter(newVal) {
      if (!newVal && this.hasToken) {
        this.$message.warning('未获取到当前登录人信息，请重新登录！')
        router.push({ path: '/login' })
      }
    }
  },
  created() {
    this.getImporterName()
  },
  methods: {
    // 获取导入人（统一逻辑，增加容错）
    getImporterName() {
      const userName = localStorage.getItem('adminName')
      if (userName && userName.trim() && userName !== 'undefined') {
        this.importerName = userName.trim()
      } else {
        this.$message.warning('未获取到当前登录人信息，请重新登录！')
        router.push({ path: '/login' })
      }
    },

    // 登录校验（统一逻辑，含token和导入人双重校验）
    checkLoginStatus() {
      if (!this.hasToken) {
        this.$message.error('登录状态已过期，请重新登录！')
        // 统一：跳转登录页并携带当前地址，支持登录后返回
        router.push({
          path: '/login',
          query: { redirect: this.$route.fullPath }
        })
        return false
      }
      if (!this.hasValidImporter) {
        this.getImporterName()
        return false
      }
      return true
    },

    // 文件校验（保持单Sheet特性，统一错误提示风格）
    beforeUpload(file) {
      const fileType = file.name.split('.').pop().toLowerCase()
      const isExcel = fileType === 'xlsx' || fileType === 'xls'
      const isLt1000M = file.size / 1024 / 1024 < 1000

      if (!isExcel) {
        this.$message.error('仅支持上传 .xlsx / .xls 格式文件！')
        return false
      }
      if (!isLt1000M) {
        this.$message.error('文件大小不能超过 1000MB！')
        return false
      }
      return true
    },

    // 文件变更（统一：重置数据，包含新增的sheetName）
    handleFileChange(file, fileList) {
      this.fileList = fileList.slice(-1)
      this.rawFile = this.fileList[0]?.raw || null
      this.parsedData = []
      this.sheetName = '' // 重置Sheet名称
      this.parseProgress = 0
      this.resetPagination()
    },

    // 移除文件（统一：清空所有相关数据）
    handleFileRemove() {
      this.fileList = []
      this.rawFile = null
      this.parsedData = []
      this.sheetName = ''
      this.parseProgress = 0
      this.resetPagination()
      this.$message.info('已移除文件')
    },

    // 超出文件数量限制提示（统一提示文案风格）
    handleExceed() {
      this.$message.warning('仅支持上传 1 个 Excel 文件，请先移除已选择的文件！')
    },

    // 解析Excel（适配单Sheet，统一日期处理+数据结构）
    async parseExcel() {
      if (!this.checkLoginStatus()) return
      if (!this.rawFile) {
        this.$message.warning('请先选择要解析的Excel文件！')
        return
      }

      this.isParsing = true
      this.parseProgress = 0
      try {
        const arrayBuffer = await this.readFileAsArrayBuffer(this.rawFile)
        const workbook = XLSX.read(arrayBuffer, {
          type: 'array',
          cellDates: true, // 统一：开启日期转Date对象
          cellText: false, // 统一：关闭纯文本转换
          dateNF: 'yyyy-mm-dd', // 统一：日期格式
          cellStyles: false,
          sheetStubs: false,
          raw: false // 统一：确保cellDates生效
        })

        // 单Sheet校验（保持业务特性）
        if (workbook.SheetNames.length !== 1) {
          this.$message.error(`串码导入只允许 1 个Sheet，当前有 ${workbook.SheetNames.length} 个，请检查文件！`)
          this.isParsing = false
          return
        }

        // 解析单Sheet数据（统一日期处理逻辑）
        this.sheetName = workbook.SheetNames[0] // 记录原始Sheet名称
        const worksheet = workbook.Sheets[this.sheetName]
        const sheetData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: '',
          blankrows: false
        })

        // 空数据校验
        if (!sheetData || sheetData.length <= 1) {
          this.parsedData = []
          this.sheetName = ''
          this.$message.warning('文件内无有效数据（需包含表头和至少1行数据）！')
          this.isParsing = false
          return
        }

        // 表头处理（统一：空表头自动命名）
        const header = sheetData[0].map((item, i) => {
          return item ? item.toString().trim() : `串码列${i + 1}`
        })

        // 行数据处理（统一Excel日期1900bug+时区校正）
        const dataList = sheetData.slice(1).map(row => {
          const obj = {}
          header.forEach((key, idx) => {
            let val = row[idx] ?? ''
            if (val instanceof Date) {
              try {
                // 统一：补偿Excel 1900闰年bug（+1天）
                const excelBugFixTime = val.getTime() + 86400000
                // 统一：东八区时区校正（+8小时）
                const gmt8Time = new Date(excelBugFixTime + (8 * 60 * 60 * 1000))
                // 统一：生成标准yyyy-MM-dd格式
                const year = gmt8Time.getUTCFullYear()
                const month = String(gmt8Time.getUTCMonth() + 1).padStart(2, '0')
                const day = String(gmt8Time.getUTCDate()).padStart(2, '0')
                val = `${year}-${month}-${day}`
              } catch (e) {
                val = val.toISOString().split('T')[0]
              }
            }
            // 统一：字符串值去空格，避免脏数据
            obj[key] = typeof val === 'string' ? val.trim() : val
          })
          return obj
        })

        this.parsedData = dataList
        this.parseProgress = 100 // 单Sheet解析完成直接设为100%
        await new Promise(r => setTimeout(r, 100)) // 保持进度条展示效果
        this.resetPagination()
        this.setCurrentPageData()
        this.$message.success(`解析成功！共 ${this.parsedData.length} 条串码数据（Sheet名：${this.sheetName}）`)
      } catch (err) {
        this.$message.error(`Excel解析失败：${err.message || '文件损坏、格式异常或正在被占用'}`)
        console.error('串码Excel解析异常：', err)
      } finally {
        this.isParsing = false
        this.parseProgress = 0
      }
    },

    // File转ArrayBuffer（统一方法，保持错误提示一致）
    readFileAsArrayBuffer(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = e => resolve(e.target.result)
        reader.onerror = () => reject(new Error('Excel文件读取失败，请检查文件是否损坏、未关闭或正在被占用'))
        reader.onabort = () => reject(new Error('文件读取被中断'))
        reader.readAsArrayBuffer(file)
      })
    },

    // 提交导入：核心调整！与所有导入场景统一数据结构
    async submitImport() {
      if (!this.checkLoginStatus()) return
      if (this.parsedData.length === 0) {
        this.$message.warning('请先解析Excel数据再提交！')
        return
      }

      try {
        const totalCount = this.parsedData.length
        // 统一：确认弹窗风格，包含导入人、数据量信息
        const confirmRes = await this.$confirm(
          `确定要提交串码数据导入吗？共${totalCount}条数据，导入人：${this.importerName}，导入后将不可撤回！`,
          '导入确认',
          {
            confirmButtonText: '确定导入',
            cancelButtonText: '取消',
            type: 'warning',
            confirmButtonClass: 'el-button--danger'
          }
        )
        if (confirmRes !== 'confirm') return

        this.isImporting = true
        this.isTableLoading = true

        // 核心：与基础表/业务表/辅助表完全统一的提交数据结构
        const submitData = {
          excelName: this.fileList[0].name, // 统一：Excel文件名
          importerName: this.importerName, // 统一：导入人（数据隔离标识）
          // 统一：sheetDatas数组结构（即使单Sheet也保持数组，兼容后端通用解析逻辑）
          sheetDataList: [{
            sheetIndex: 1, // 统一：Sheet索引（单Sheet固定为1）
            originalSheetName: this.sheetName || '串码数据', // 统一：原始Sheet名称
            sheetName: 'Sheet1', // 统一：标准化Sheet名
            data: this.parsedData // 统一：解析后的行数据
          }]
        }
        console.log('准备提交串码导入数据（统一结构）：', submitData)

        // 调用串码导入接口（路径区分，结构统一）
        const res = await service.post('excel/import/serial-code', submitData, {
          timeout: 300000, // 统一：超时时间（5分钟）
          onUploadProgress: (progressEvent) => { // 统一：上传进度监听
            const percent = Math.round((progressEvent.loaded / (progressEvent.total || 1)) * 100)
            console.log('串码数据上传进度：', percent, '%')
          }
        })

        if (res?.code === 200) {
          this.$message.success('串码导入成功！')
          this.handleFileRemove() // 统一：导入成功后清空数据
        } else {
          this.$message.error(`导入失败：${res?.msg || '后端处理失败，未返回有效结果'}`)
        }
      } catch (e) {
        // 统一：异常分类处理（取消导入/其他异常）
        if (e === 'cancel' || (e.name === 'Error' && e.message === 'cancel')) {
          this.$message.info('已取消串码数据导入')
        } else {
          // 过滤全局401拦截的错误，避免重复提示
          if (e.message !== '登录状态失效，请重新登录') {
            this.$message.error(`导入请求异常：${e.message || '网络异常、请求超时或后端接口未响应'}`)
            console.error('串码导入请求异常详情：', e)
          }
        }
      } finally {
        this.isImporting = false
        this.isTableLoading = false
      }
    },

    // 重置分页参数（统一方法）
    resetPagination() {
      this.pagination = { currentPage: 1, pageSize: 100 }
    },

    // 计算当前页数据（统一逻辑）
    setCurrentPageData() {
      if (!this.parsedData.length) {
        this.currentPageData = []
        return
      }
      const { currentPage, pageSize } = this.pagination
      const startIndex = (currentPage - 1) * pageSize
      const endIndex = currentPage * pageSize
      this.currentPageData = this.parsedData.slice(startIndex, endIndex)
    },

    // 切换单页展示条数（统一方法）
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.currentPage = 1
    },

    // 切换当前页码（统一方法）
    handleCurrentChange(val) {
      this.pagination.currentPage = val
    }
  }
}
</script>

<style scoped>
/* 样式完全保持统一，仅保留串码导入专属文本 */
.excel-import-container {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: calc(100vh - 120px);
}
.import-card {
  padding: 30px;
}
.importer-info {
  margin-bottom: 20px;
  padding: 10px 15px;
  background-color: #f0f9ff;
  border-radius: 6px;
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #333;
}
.importer-info .el-icon-user {
  color: #409eff;
  margin-right: 8px;
  font-size: 16px;
}
.importer-info .label {
  color: #666;
}
.importer-info .name {
  font-weight: 500;
  color: #409eff;
  margin-left: 4px;
}
.upload-section {
  margin-bottom: 30px;
}
.excel-uploader {
  width: 100%;
  height: 200px;
  line-height: 200px;
  text-align: center;
  border: 2px dashed #dcdcdc;
  border-radius: 6px;
  margin-bottom: 20px;
  cursor: pointer;
  transition: all 0.3s;
}
.excel-uploader:hover {
  border-color: #409eff;
  background-color: #f8f9fa;
}
.upload-icon {
  font-size: 48px;
  color: #409eff;
  margin-bottom: 10px;
  display: block;
  line-height: 1;
}
.custom-file-list {
  margin-bottom: 20px;
}
.file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 15px;
  border: 1px solid #e6e6e6;
  border-radius: 6px;
  background-color: #fff;
}
.file-icon {
  font-size: 20px;
  color: #67c23a;
  margin-right: 12px;
}
.file-name {
  flex: 1;
  font-size: 14px;
  color: #333;
  white-space: nowrap;
  overflow: auto;
  text-overflow: ellipsis;
}
.delete-btn {
  color: #f56c6c;
}
.btn-group {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
}
.btn-group .el-button {
  margin: 0 10px 10px;
}
.preview-section {
  margin-top: 20px;
}
.pagination-container {
  margin-top: 15px;
  text-align: right;
}
.data-count-tip {
  margin-top: 10px;
  padding: 8px 12px;
  background-color: #fff2f2;
  border: 1px solid #ffccc7;
  border-radius: 4px;
  color: #f56c6c;
  font-size: 12px;
  display: flex;
  align-items: center;
}
.data-count-tip.info {
  background-color: #fffbee;
  border-color: #ffe58f;
  color: #fa8c16;
}
</style>